import { useEffect, useState } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AppShell } from "./shared/layout/AppShell";
import { VersionBadge } from "./shared/ui";
import { apiUrl } from "./shared/api";
import { OrdiniPage } from "./features/ordini/OrdiniPage";
import { OrderDetailPage } from "./features/ordini/OrderDetailPage";
import { MessaggiPage } from "./features/messaggi/MessaggiPage";
import { FornitoriPage } from "./features/fornitori/FornitoriPage";
import { ImpostazioniPage } from "./features/impostazioni/ImpostazioniPage";
import { IstruzioniPage } from "./features/istruzioni/IstruzioniPage";
import { HealthPage } from "./features/health/HealthPage";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { OnboardingPage } from "./features/onboarding/OnboardingPage";
import { ResiPage } from "./features/resi/ResiPage";
import { RitiriPage } from "./features/ritiri/RitiriPage";
import { ReclamiPage } from "./features/reclami/ReclamiPage";
import { MailPage } from "./features/mail/MailPage";
import { ClientiPage } from "./features/clienti/ClientiPage";
import { ClienteDetailPage } from "./features/clienti/ClienteDetailPage";
import { FatturazionePage } from "./features/fatturazione/FatturazionePage";
import { CorrispettiviAmazonPage } from "./features/fatturazione/CorrispettiviAmazonPage";
import { FattureEsterePage } from "./features/fatturazione/FattureEsterePage";
import { FattureFornitoriPage } from "./features/fatturazione/FattureFornitoriPage";
import { ControlliFatturePage } from "./features/fatturazione/ControlliFatturePage";
import { SaldiPage } from "./features/fatturazione/SaldiPage";
import { LoginPage } from "./features/auth/LoginPage";
import { PermessiProvider } from "./shared/auth/permessi";

type ReadyBody = {
  status?: "ready" | "not_ready";
  onboarding_complete?: boolean;
};

function StartRoute() {
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    // Race condition all'avvio: il backend Python packaged ci mette ~2-5
    // secondi per partire. Una singola chiamata a /health/ready che fallisce
    // a livello di rete ci spedirebbe a /onboarding anche con onboarding
    // gia' completo. Distinguo tre casi:
    //   - Risposta valida (ok o 503 con body): decido subito, niente retry.
    //   - Errore di rete/fetch: il backend non e' ancora up, retry fino a 30s.
    let active = true;
    let attempts = 0;
    let timerId: number | null = null;
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 2000;

    async function check(): Promise<void> {
      let response: Response;
      try {
        response = await fetch(apiUrl("/health/ready"));
      } catch {
        if (!active) return;
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          // Backend irraggiungibile dopo i retry: l'app installata è quasi
          // sempre già onboardata e il launcher attende /health prima di
          // aprire la finestra, quindi landiamo comunque su Ordini
          // (richiesta esplicita Davide: "l'app si apra nella pagina
          // ordini"). L'onboarding resta raggiungibile se mai servisse.
          setTarget("/ordini");
          return;
        }
        // Memorizziamo l'id cosi' il cleanup puo' cancellarlo se l'utente
        // smonta il componente prima che il timer scatti (audit HIGH-4
        // 2026-04-29 — react Strict Mode doppia-mount esponeva il leak).
        timerId = window.setTimeout(() => {
          if (active) void check();
        }, INTERVAL_MS);
        return;
      }
      // Backend ha risposto. Estraggo lo status dal body (sia 200 che 503
      // ritornano lo stesso shape; il 503 wrappa sotto "detail").
      let onboardingComplete: boolean | undefined;
      try {
        const body = (await response.json()) as ReadyBody & { detail?: ReadyBody };
        // Sia il 200 (ready) che il 503 (not_ready) ritornano lo stesso
        // shape; il 503 lo wrappa sotto ``detail``.
        onboardingComplete = body.onboarding_complete ?? body.detail?.onboarding_complete;
      } catch {
        onboardingComplete = undefined;
      }
      if (!active) return;
      // Landing post-startup: SEMPRE Ordini (richiesta Davide 2026-05-15:
      // "l'app si apra direttamente nella pagina ordini"). Il workflow
      // operativo reale e' sugli ordini da evadere; Dashboard/altre tab
      // restano raggiungibili dalla nav. Mandiamo a /onboarding SOLO se
      // l'onboarding non e' mai stato completato (primo avvio genuino):
      // un utente gia' configurato a cui manca una credenziale opzionale
      // (status=not_ready ma onboarding_complete=true) NON deve essere
      // intrappolato nel wizard — gestisce le credenziali da Impostazioni.
      setTarget(onboardingComplete === false ? "/onboarding" : "/ordini");
    }
    void check();
    return () => {
      active = false;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, []);

  if (target) return <Navigate to={target} replace />;
  // Audit MEDIUM-3: feedback durante il polling /health/ready (fino a
  // ~30s a freddo) invece di shell vuota muta.
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "#807A70",
        fontSize: 13.5,
      }}
    >
      Avvio in corso…
    </div>
  );
}

// Audit LOW-2: calcolato a module-load (window.location.protocol e'
// stabile). Dentro App() React vedeva un nuovo component type ad ogni
// re-render → rischio unmount/remount dell'intero router.
const Router = window.location.protocol === "file:" ? HashRouter : BrowserRouter;

/** key={ref} sulla pagina ordine: al cambio di :ref (es. i link «Altri ordini»
 *  del customer-360, stessa route) React rimonta OrderDetailPage da zero, così
 *  editor colli, tracking e stato interno non ereditano l'ordine PRECEDENTE
 *  (bug-hunt 2026-07-13: senza remount «Salva/Genera LDV» scriveva colli e
 *  dimensioni dell'ordine A su un ordine B — LDV reale sbagliata). */
function OrderDetailRoute() {
  const { ref } = useParams<{ ref: string }>();
  return <OrderDetailPage key={ref ?? ""} />;
}

export function App() {
  return (
    <Router>
      <Routes>
        <Route path="/health" element={<HealthPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        {/* Login web-mode: pagina piena FUORI dall'AppShell (niente rail).
            Sul desktop la route è inerte: nessun gate 401 ci porta qui. */}
        <Route path="/login" element={<LoginPage />} />
        {/* I permessi si caricano DENTRO l'area autenticata: fuori (login,
            onboarding, health) `/me` darebbe 401 e la fetch sarebbe sprecata. */}
        <Route
          element={
            <PermessiProvider>
              <AppShell />
            </PermessiProvider>
          }
        >
          <Route index element={<StartRoute />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ordini" element={<OrdiniPage />} />
          <Route path="/ordini/:ref" element={<OrderDetailRoute />} />
          {/* Il Catalogo è confluito nella pagina Fornitori a tab
              (decisione Davide 2026-07-03): redirect per bookmark/deep-link. */}
          <Route path="/catalogo" element={<Navigate to="/fornitori" replace />} />
          <Route path="/messaggi" element={<MessaggiPage />} />
          <Route path="/clienti" element={<ClientiPage />} />
          <Route path="/clienti/:key" element={<ClienteDetailPage />} />
          <Route path="/fatturazione" element={<FatturazionePage />} />
          <Route path="/fatturazione/corrispettivi" element={<CorrispettiviAmazonPage />} />
          <Route path="/fatturazione/fatture-estere" element={<FattureEsterePage />} />
          <Route path="/fatturazione/fatture-fornitori" element={<FattureFornitoriPage />} />
          <Route path="/fatturazione/controlli" element={<ControlliFatturePage />} />
          <Route path="/fatturazione/saldi" element={<SaldiPage />} />
          <Route path="/resi" element={<ResiPage />} />
          <Route path="/ritiri" element={<RitiriPage />} />
          <Route path="/reclami" element={<ReclamiPage />} />
          <Route path="/mail/:ref?" element={<MailPage />} />
          <Route path="/fornitori" element={<FornitoriPage />} />
          <Route path="/impostazioni" element={<ImpostazioniPage />} />
          <Route path="/istruzioni" element={<IstruzioniPage />} />
        </Route>
      </Routes>
      <VersionBadge />
    </Router>
  );
}
